import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Body, PageHeader, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { CheckboxWithBoldLabel } from 'src/presentation/components/forms/Checkbox';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import rolesResource, { Permission, Role } from 'src/actions/roles/rolesResource';
import { PERMISSION_LABELS, SPLIT_AGENT_PERMISSIONS, getPermissionGroups } from 'src/actions/roles/permissionGroups';
import { STATUS_PERSISTING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import { AccessibleCheckboxWrapper } from './RoleEditor.styles';

const RoleEditor = ({ errors, item, root }: FormEditorProps) => {
  const [isComplete, setComplete] = useState<boolean>();

  const { replace, validate } = rolesResource.effects;
  const { resetErrors } = rolesResource.actions;

  const { persistingState } = useResourceValues(rolesResource);

  const formContext = useForm({
    add: replace,
    errors,
    defaultItem: {
      permissions: [],
    },
    item,
    resetErrors,
    root,
    update: replace,
    validate,
  });

  const { isNew, onClose, onSave } = formContext;

  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  useEffect(() => {
    if (persistingState === STATUS_SUCCEEDED) {
      setComplete(true);
    }
  }, [persistingState, setComplete]);

  const isFeatureEnabled = useIsFeatureEnabled();
  const customRolesV2 = isFeatureEnabled('customRolesV2');
  const builtin = formContext.workingState.builtin;
  const permissionGroups = getPermissionGroups({ builtin, customRolesV2 });

  const disabled = useMemo(
    () => formContext.workingState.builtin || persistingState === STATUS_PERSISTING,
    [formContext.workingState, persistingState]
  );

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <PageHeader
          headerText={`${isNew ? 'Add' : 'Edit'} Role`}
          descriptionText={`${isNew ? 'Create' : 'Edit'} the Role and assign Permissions.`}
        />
        <Body>
          <Row>
            <InputField
              attr="name"
              data-aid="name"
              description="This name will be displayed when editing Users."
              disabled={disabled}
              label="Role name"
              placeholder="Enter the role name"
            />
          </Row>
          <Row>
            <InputField
              attr="description"
              data-aid="description"
              disabled={disabled}
              label="Description"
              placeholder="Enter role description"
            />
          </Row>

          <Section
            header="Permissions"
            description="Select the permissions that should be granted to users with this role."
          >
            <AccessibleCheckboxWrapper>
              {permissionGroups.map((group, index) => (
                <Section key={group.name} header={index === 0 ? undefined : group.name}>
                  <PermissionsSection permissions={group.permissions} disabled={disabled} />
                </Section>
              ))}
            </AccessibleCheckboxWrapper>
          </Section>
        </Body>

        <Footer>
          <Button data-aid="cancel-button" type={Button.Types.TEXT} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-aid="save-button"
            disabled={!isModified}
            isLoading={persistingState === STATUS_PERSISTING}
            onClick={onSave}
          >
            Save
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Role"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

const PermissionsSection = ({ permissions, disabled }: { permissions: Permission[]; disabled: boolean }) => {
  const { workingState, updateState } = useContext(FormContext);

  const value = useMemo(() => new Set((workingState as Role).permissions || []), [workingState]);

  const handleChange = (permission: Permission, checked: boolean) => {
    if (permission === 'agent' && checked) {
      SPLIT_AGENT_PERMISSIONS.forEach(permission => {
        value.add(permission as Permission);
      });
      value.add('agent');
    } else if (permission === 'agent' && !checked) {
      SPLIT_AGENT_PERMISSIONS.forEach(permission => {
        value.delete(permission as Permission);
      });
      value.delete('agent');
    } else if (SPLIT_AGENT_PERMISSIONS.some(p => p === permission) && !checked) {
      value.delete(permission);
      value.delete('agent');
    } else {
      if (checked) {
        value.add(permission);
      } else {
        value.delete(permission);
      }
    }

    updateState({ permissions: Array.from(value).sort() });
  };

  return (
    <>
      {permissions.map(permission => {
        const permissionInfo = PERMISSION_LABELS[permission];
        return (
          <Row key={permission}>
            <CheckboxWithBoldLabel
              attr="permissions"
              data-aid={`permission-${permission}`}
              description={permissionInfo.description}
              disabled={disabled}
              label={permissionInfo.label}
              onChange={checked => handleChange(permission, checked)}
              value={value.has(permission) ? 'checked' : ''}
            />
          </Row>
        );
      })}
    </>
  );
};

export default RoleEditor;
